import { useState, useCallback } from 'react';
import { useApiService } from './useApiService';
import { Pool, PoolUpdate } from '../types';
import { useSnackbar } from 'notistack';

interface UsePoolFormModalProps {
  onSuccess?: () => void; // Optional callback on successful submission
}

interface UsePoolFormModalReturn {
  isModalOpen: boolean;
  editingPool: Pool | null;
  openModal: (pool?: Pool) => void;
  closeModal: () => void;
  handleSubmit: (data: PoolUpdate) => Promise<void>;
  isSubmitting: boolean;
}

export const usePoolFormModal = ({
  onSuccess,
}: UsePoolFormModalProps = {}): UsePoolFormModalReturn => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPool, setEditingPool] = useState<Pool | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { poolApi } = useApiService();

  const openModal = useCallback((pool?: Pool) => {
    console.log('[usePoolFormModal] Opening modal. Pool:', pool);
    setEditingPool(pool || null);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    console.log('[usePoolFormModal] Closing modal.');
    setIsModalOpen(false);
    setEditingPool(null); // Reset editing pool on close
  }, []);

  const handleSubmit = useCallback(async (data: PoolUpdate) => {
    console.log('Form submission initiated with data:', data);
    setIsSubmitting(true);

    // 필수 필드 검증
    const requiredFields = ['name', 'manager_name', 'manager_phone', 'address', 'type', 'location', 'capacity'];
    const missingFields = requiredFields.filter(field => {
      if (field === 'capacity') {
        return data[field] === undefined || data[field] === null || data[field] <= 0;
      }
      return !data[field];
    });

    if (missingFields.length > 0) {
      console.error('Missing required fields:', missingFields);
      enqueueSnackbar(`다음 필수 필드를 입력: ${missingFields.join(', ')}`, { variant: 'error' });
      setIsSubmitting(false);
      return;
    }

    // capacity가 숫자인지 확인
    if (isNaN(Number(data.capacity)) || Number(data.capacity) <= 0) {
      console.error('Invalid capacity value:', data.capacity);
      enqueueSnackbar('수용 인원은 1명 이상의 숫자여야', { variant: 'error' });
      setIsSubmitting(false);
      return;
    }
    
    try {
      let response;
      
      // 버튼을 클릭할 때마다 기록
      console.log('[usePoolFormModal] Edit 버튼 클릭됨!');
      
      if (editingPool) {
        // 불필요한 필드 제거 (프론트엔드 전용 필드)
        const cleanData = { ...data };
        if ('dataSource' in cleanData) {
          console.log('[usePoolFormModal] API 요청에서 dataSource 필드 제거');
          delete cleanData.dataSource;
        }
        
        // DB에서 NOT NULL로 설정된 필수 필드 검증
        const requiredFields = ['name', 'manager_name', 'manager_phone', 'address', 'type', 'location', 'capacity'];
        const missingFields = requiredFields.filter(field => 
          cleanData[field] === undefined || 
          cleanData[field] === null || 
          (typeof cleanData[field] === 'string' && cleanData[field].trim() === '')
        );

        if (missingFields.length > 0) {
          console.error('[usePoolFormModal] [ERROR] 필수 필드 누락:', missingFields);
          enqueueSnackbar(`다음 필수 항목이 누락됨: ${missingFields.join(', ')}`, { variant: 'error' });
          setIsSubmitting(false);
          return; // 제출 중단
        }
        
        // ID 기반 업데이트가 가능한 경우
        if (editingPool.id) {
          console.log(`[usePoolFormModal] Updating pool via updatePool - ID: ${editingPool.id}`);
          response = await poolApi.updatePool(Number(editingPool.id), cleanData);
        } 
        // ID가 없고 코드만 있는 경우, 코드로 바로 업데이트
        else if (editingPool.code) {
          console.log(`[usePoolFormModal] Updating pool via code: ${editingPool.code}`);
          
          try {
            // 먼저 해당 시설의 정보를 가져옵니다
            // 주의: getPoolById는 ID를 사용하므로 code를 ID로 변환하거나 다른 방법 필요
            // API 호출부를 확인하여 적절한 메서드 사용
            // 대체 방법: 직접 코드로 API 호출 또는 ID 추정
            
            // 임시 방법: id가 없으면 code 값을 id로 사용 가정 (실제 구현은 백엔드 API에 맞게 조정)
            // 숫자를 추출하고 없으면 1을 기본값으로 사용 
            const numericPart = editingPool.code.replace(/[^0-9]/g, '');
            const poolId = Number(editingPool.id || (numericPart ? numericPart : 1));
            console.log(`[usePoolFormModal] Using estimated pool ID: ${poolId} for code: ${editingPool.code}`);
            
            // 시설 정보 업데이트
            response = await poolApi.updatePool(poolId, cleanData);
          } catch (error) {
            console.error(`[usePoolFormModal] 시설 정보 업데이트 실패: ${error.message}`);
            throw new Error(`시설 코드(${editingPool.code})로 정보를 업데이트 불가: ${error.message}`);
          }
        } 
        else {
          throw new Error('시설 ID나 코드가 없어 업데이트 불가');
        }
        
        console.log('[usePoolFormModal] [DEBUG] 업데이트 응답 전체:', JSON.stringify(response, null, 2));
        console.log('[usePoolFormModal] [DEBUG] 응답에 success 필드가 있나요?', 'success' in response);
        console.log('[usePoolFormModal] [DEBUG] success 필드 값:', response.success);
        console.log('[usePoolFormModal] [DEBUG] 응답 데이터:', response.data);
        console.log('[usePoolFormModal] [DEBUG] 응답 메시지:', response.message);
        console.log('[usePoolFormModal] [DEBUG] 응답 상태 코드:', response.status);
      } else if (!editingPool) {
        // 불필요한 필드 제거 (프론트엔드 전용 필드)
        const cleanData = { ...data };
        if ('dataSource' in cleanData) {
          console.log('[usePoolFormModal] API 요청에서 dataSource 필드 제거');
          delete cleanData.dataSource;
        }
        
        // DB에서 NOT NULL로 설정된 필수 필드 검증 (새 시설 생성 시에는 password도 필수)
        const requiredFields = ['name', 'manager_name', 'manager_phone', 'address', 'type', 'location', 'capacity']; // 'password' 필드 제외
        const missingFields = requiredFields.filter(field => 
          cleanData[field] === undefined || 
          cleanData[field] === null || 
          (typeof cleanData[field] === 'string' && cleanData[field].trim() === '')
        );

        if (missingFields.length > 0) {
          console.error('[usePoolFormModal] [ERROR] 필수 필드 누락:', missingFields);
          enqueueSnackbar(`다음 필수 항목이 누락됨: ${missingFields.join(', ')}`, { variant: 'error' });
          setIsSubmitting(false);
          return; // 제출 중단
        }
        
        // Create new pool
        console.log('[usePoolFormModal] Creating new pool.');
        console.log('[usePoolFormModal] Request data:', JSON.stringify(cleanData, null, 2));
        
        try {
          // API 직접 호출 방식으로 변경 (tempId 사용 안 함)
          response = await poolApi.createPool(cleanData);
          console.log('[usePoolFormModal] [DEBUG] createPool 응답 전체:', JSON.stringify(response, null, 2));
          
          // 응답이 없는 경우 처리
          if (!response) {
            throw new Error('API 응답 없음');
          }
          
          // 여기서 response를 바로 검사하지 않고 상위 로직에서 통합 처리하도록 수정
          // 이 부분은 API 호출 성공 시 응답 로깅만 수행
        } catch (apiError) {
          console.error('[usePoolFormModal] API 호출 오류:', apiError);
          
          // 더 자세한 에러 로깅
          if (apiError.response) {
            console.error('[usePoolFormModal] 에러 응답 상태:', apiError.response.status);
            console.error('[usePoolFormModal] 에러 응답 데이터:', apiError.response.data);
          }
          
          throw apiError; // 상위 catch 블록에서 처리하도록 다시 throw
        }
      } else {
        // Handle case where editingPool exists but has no code
        console.error('[usePoolFormModal] Cannot update pool: Editing pool exists but has no ID or code.', editingPool);
        enqueueSnackbar('시설 ID나 코드가 없어 업데이트 불가', { variant: 'error' });
        setIsSubmitting(false);
        return; // Stop submission
      }

      console.log('[usePoolFormModal] [DEBUG] API Response:', JSON.stringify(response, null, 2));
      
      // id가 있는 경우 성공으로 처리
      const isSuccessResponse = response && response.id;
      
      console.log('[usePoolFormModal] [DEBUG] 최종 성공 여부 결정:', isSuccessResponse);
      
      if (isSuccessResponse) {
        console.log('[usePoolFormModal] API call 성공! onSuccess 콜백 호출 예정');
        enqueueSnackbar(`시설 정보가 성공적으로 ${editingPool ? '수정' : '추가'}됨`, { variant: 'success' });
        
        // 먼저 모달을 닫고
        closeModal();
        
        // 약간의 지연 후 onSuccess 콜백 실행 (백엔드 처리 시간 고려)
        console.log('[usePoolFormModal] Modal closed, now calling onSuccess callback if provided');
        setTimeout(() => {
          if (onSuccess) {
            console.log('[usePoolFormModal] onSuccess callback exists, calling it now');
            onSuccess(); // Call the success callback if provided
            console.log('[usePoolFormModal] onSuccess callback called');
          } else {
            console.log('[usePoolFormModal] No onSuccess callback provided');
          }
        }, 300);
        return; // 성공 시 여기서 함수 종료
      }
      
      // 실패 처리
      console.log('[usePoolFormModal] [ERROR] API 요청 실패:', response);
      enqueueSnackbar(response?.message || `시설 정보 ${editingPool ? '수정' : '추가'} 중 오류 발생`, { variant: 'error' });
    } catch (error) {
      console.error('[usePoolFormModal] [ERROR] Submission error:', error);
      enqueueSnackbar('요청 처리 중 오류 발생', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }, [editingPool, closeModal, onSuccess, enqueueSnackbar, poolApi]);

  return {
    isModalOpen,
    editingPool,
    openModal,
    closeModal,
    handleSubmit,
    isSubmitting,
  };
};